import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismCheckbox,
  PrismInput,
  PrismLoadingBar,
} from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { not } from 'ember-truth-helpers';
import isValidEmailAddress from 'frontend-utils/utils/is-valid-email-address';
import GeneralDropdownsMultipleSelectDropdownAjax from 'lumen/components/general/dropdowns/multiple-select-dropdown-ajax';
import GeneralFormFieldsCancelAndConfirm from 'lumen/components/general/form-fields/cancel-and-confirm';

interface SettingsHotelAccessFormsFormAddUserToHotelSignature {
  Args: {
    closeModal: unknown;
    doRefresh: unknown;
  };
}

export default class SettingsHotelAccessFormsFormAddUserToHotel extends Component<SettingsHotelAccessFormsFormAddUserToHotelSignature> {
  @service declare access: Services['access'];
  @service declare ajax: Services['ajax'];
  @service declare alerting: Services['alerting'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked copyReports = false;
  @tracked emailAddress = null;
  @tracked excludeHotels = false;
  @tracked firstName = null;
  @tracked hotelOptions = [];
  @tracked hotels = [];
  @tracked isLoading = false;
  @tracked lastName = null;
  @tracked totalCount = 0;
  @tracked visibleItems = 0;

  getAutocompleteHotelsTask = task({ keepLatest: true }, async (options) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const page = options?.page ?? 0;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const query = options?.query ?? '';
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const userId = options?.userId;

    const data = {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      page,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      q: query,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      user: userId,
    };

    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { meta, results } = await this.ajax.request(
        'autocomplete/subscription_new_user',
        {
          data,
          method: 'GET',
        },
      );

      const newOptions = this.hotelAndMarketService
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        .formatAutocompleteResults(results)
        .map((hotel) => {
          const { hotel_id, name, products, subscription_type } = hotel;

          return {
            badge: subscription_type,
            description: products.join(', '),
            id: hotel_id,
            name,
          };
        });

      // @ts-expect-error: Incorrect type
      this.hotelOptions =
        page === 0 ? newOptions : [...this.hotelOptions, ...newOptions];

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      this.totalCount = meta.results_count;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      this.visibleItems = meta.results_per_page;
    } catch {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.alerting.showErrorAlert({
        title: this.intl.t('settings.hotel_access.update_failed'),
      });
    }
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get allHotelsAreSelected() {
    return this.excludeHotels && this.hotels.length === 0;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isEmailAddressValid() {
    // @ts-expect-error: Incorrect type
    return isValidEmailAddress(this.emailAddress);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSaveButtonDisabled() {
    const isFormValid =
      !this.isLoading &&
      Boolean(this.firstName) &&
      Boolean(this.lastName) &&
      this.isEmailAddressValid &&
      (this.hotels.length > 0 || this.allHotelsAreSelected);

    return !isFormValid;
  }

  constructor() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line prefer-rest-params
    super(...arguments);

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.getAutocompleteHotelsTask.perform();
  }

  @action
  addHotelAccessRight(): void {
    this.isLoading = true;

    const data = {
      add_all_hotels: this.allHotelsAreSelected,
      copy_reports: this.copyReports,
      exclude: this.excludeHotels,
      user: {
        email: this.emailAddress,
        first_name: this.firstName,
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        hotels: this.hotels.map((hotel) => hotel.id),
        last_name: this.lastName,
      },
    };

    this.ajax
      .post('/users/', {
        data: JSON.stringify(data),
        contentType: 'application/json',
      })
      .then((response) => {
        this.isLoading = false;

        // @ts-expect-error: Incorrect type
        this.args.doRefresh();

        this.toast.success(
          this.intl.t('settings.hotel_access.user_save_succes'),
          {
            showIcon: true,
          },
        );

        if (this.copyReports) {
          this.toast.success(
            this.intl.t('settings.reports.copy_reports_in_progress', {
              email: this.emailAddress,
            }),
            { showIcon: true },
          );
        }

        this.usageAnalytics.track('New User Added', {
          eventTrigger: 'settings-button',
          newUserAdded: true,
        });

        // @ts-expect-error: Incorrect type
        this.args.closeModal();

        this.router.transitionTo('hotel.settings.hotels.edit', {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          queryParams: { userId: response.user.id },
        });
      });
  }

  @action handleHotelsChange(
    // @ts-expect-error: Incorrect type
    options,
    // @ts-expect-error: Incorrect type
    { unsafe_isExcludeMode: isExcludeMode },
  ): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.hotels = options;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.excludeHotels = isExcludeMode;
  }

  // @ts-expect-error: Incorrect type
  @action handleLoadMoreHotels(params): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { page, q: query } = params;

    // eslint-disable-next-line @typescript-eslint/no-floating-promises, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-assignment
    this.getAutocompleteHotelsTask.perform({ page, query });
  }

  @action toggleCopyReports(): void {
    this.copyReports = !this.copyReports;
  }

  // @ts-expect-error: Incorrect type
  @action updateEmailAddress(event): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    this.emailAddress = event.target.value;
  }

  // @ts-expect-error: Incorrect type
  @action updateFirstName(event): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    this.firstName = event.target.value;
  }

  // @ts-expect-error: Incorrect type
  @action updateLastName(event): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    this.lastName = event.target.value;
  }

  <template>
    <div class="modal--small">
      <div class="add-user-form-content">
        {{#if this.isLoading}}
          <PrismLoadingBar />
        {{/if}}

        <div class="flex flex-row">
          <div class="mr-400 flex w-1/2 flex-col">
            <PrismInput
              @label={{t "settings.account.first_name"}}
              @onInput={{this.updateFirstName}}
              @required={{true}}
              @requiredErrorMessage={{t "general.required"}}
              {{! @glint-expect-error: Incorrect type }}
              @value={{this.firstName}}
            />
          </div>
          <div class="flex w-1/2 flex-col">
            <PrismInput
              @label={{t "settings.account.last_name"}}
              @onInput={{this.updateLastName}}
              @required={{true}}
              @requiredErrorMessage={{t "general.required"}}
              {{! @glint-expect-error: Incorrect type }}
              @value={{this.lastName}}
            />
          </div>
        </div>

        <div class="flex flex-col">
          <PrismInput
            @errorMessage={{t "settings.reports.error.invalid_email"}}
            @hasErrors={{not this.isEmailAddressValid}}
            @label={{t "settings.account.email"}}
            @onInput={{this.updateEmailAddress}}
            @required={{true}}
            @requiredErrorMessage={{t "general.required"}}
            @type="email"
            {{! @glint-expect-error: Incorrect type }}
            @value={{this.emailAddress}}
          />
        </div>

        <label class="input-label required-indicator">
          {{t "settings.hotels.select_hotels_placeholder"}}
        </label>

        <div class="hotel-market-search-wrapper flex flex-col">
          {{! @glint-expect-error: Incorrect type }}
          <GeneralDropdownsMultipleSelectDropdownAjax
            @canSelectAll={{not this.access.isStaffUser}}
            @isExcludeMode={{this.excludeHotels}}
            @onChange={{this.handleHotelsChange}}
            @onLoadMore={{this.handleLoadMoreHotels}}
            @options={{this.hotelOptions}}
            @totalCount={{this.totalCount}}
            @translationPrefix="useraccess_rights_hoteldropdown"
            @value={{this.hotels}}
            @visibleItems={{this.visibleItems}}
            data-test-id="hotel-access-hotel-select"
          />
        </div>

        <div class="layout layout-sm-column checkbox-wrapper">
          <PrismCheckbox
            @checked={{this.copyReports}}
            @onChange={{this.toggleCopyReports}}
          >
            {{t "settings.hotels.copy_reports"}}
          </PrismCheckbox>
        </div>
      </div>

      <div class="settings-modal__footer">
        <GeneralFormFieldsCancelAndConfirm
          {{! @glint-expect-error: Incorrect type }}
          @cancel={{@closeModal}}
          @confirm={{this.addHotelAccessRight}}
          @confirmText={{t "general.save"}}
          @isConfirmDisabled={{this.isSaveButtonDisabled}}
        />
      </div>
    </div>
  </template>
}
